import { makeStyles } from '@material-ui/core/styles';
import { primaryButtonStyle, secondaryButtonStyle } from '../../shared/styles/buttons.styles';
import { redesignTheme } from '../../theme';

const { colors, fonts, fontSize, spacing, radius, breakpoints } = redesignTheme;

export const useGameSynthesisStyles = makeStyles(() => ({
  topRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.sm,
    marginBottom: spacing.md,
    [`@media (min-width: ${breakpoints.tablet}px)`]: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
    },
  },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: spacing.xs,
  },
  backLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: 0,
    border: 'none',
    background: 'transparent',
    color: '#383838',
    fontFamily: 'Poppins',
    fontSize: 15,
    fontWeight: 500,
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  historyButton: secondaryButtonStyle,
  retakeButton: primaryButtonStyle,
  actionIcon: {
    width: 16,
    height: 16,
    flexShrink: 0,
  },
  hero: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.md,
    padding: '40px 48px',
    borderRadius: radius.md,
    backgroundColor: '#000B33',
    color: colors.white,
    marginBottom: spacing.lg,
    fontFamily: 'Poppins',
    [`@media (min-width: ${breakpoints.tablet}px)`]: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 28,
    },
  },
  cover: {
    width: 132,
    height: 176,
    flexShrink: 0,
    borderRadius: 10,
    overflow: 'hidden',
    backgroundColor: colors.primaryDark,
    alignSelf: 'center',
    [`@media (min-width: ${breakpoints.tablet}px)`]: {
      alignSelf: 'center',
    },
  },
  coverImage: {
    display: 'block',
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  heroMain: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  gameLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    fontFamily: 'Poppins',
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 1.4,
    textTransform: 'uppercase',
    color: 'rgba(255, 255, 255, 0.72)',
    '& span': {
      fontFamily: 'Poppins',
    },
  },
  gameLabelIcon: {
    width: 16,
    height: 16,
  },
  gameTitle: {
    margin: 0,
    fontFamily: 'Poppins',
    fontSize: 28,
    fontWeight: 700,
    lineHeight: 1.15,
    color: colors.white,
    [`@media (max-width: ${breakpoints.mobile}px)`]: {
      fontSize: fontSize.xl,
    },
    '& span': {
      fontFamily: 'Poppins',
    },
  },
  serviceName: {
    margin: 0,
    fontFamily: 'Poppins',
    fontSize: fontSize.base,
    fontWeight: 400,
    color: 'rgba(255, 255, 255, 0.72)',
    '& span': {
      fontFamily: 'Poppins',
    },
  },
  metaGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: '20px 40px',
    marginTop: 18,
    width: 'auto',
    maxWidth: '100%',
  },
  metaItem: {
    flex: '0 0 auto',
    minWidth: 0,
  },
  metaLabel: {
    margin: 0,
    fontFamily: 'Poppins',
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: 0.9,
    textTransform: 'uppercase',
    color: 'rgba(255, 255, 255, 0.55)',
    '& span': {
      fontFamily: 'Poppins',
    },
  },
  metaValue: {
    margin: '6px 0 0',
    fontFamily: 'Poppins',
    fontSize: fontSize.md,
    fontWeight: 600,
    lineHeight: 1.3,
    color: colors.white,
    '& span': {
      fontFamily: 'Poppins',
    },
  },
  gaugeBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    alignSelf: 'center',
    marginLeft: 'auto',
  },
  gaugeWrap: {
    position: 'relative',
    width: 195,
    height: 195,
  },
  gaugeTrack: {
    color: 'rgba(255, 255, 255, 0.14)',
    position: 'absolute',
    left: 0,
    top: 0,
  },
  gaugeValue: {
    color: '#22c55e',
    position: 'absolute',
    left: 0,
    top: 0,
    '& .MuiCircularProgress-circle': {
      strokeLinecap: 'round',
    },
  },
  gaugeCenter: {
    position: 'absolute',
    inset: 18,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    gap: 4,
  },
  gaugeScoreRow: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'center',
    gap: 4,
  },
  gaugeScore: {
    fontFamily: 'Poppins',
    fontSize: 48,
    fontWeight: 800,
    color: colors.white,
    lineHeight: 1,
    '& span': {
      fontFamily: 'Poppins',
    },
  },
  gaugeMax: {
    fontFamily: 'Poppins',
    fontSize: '20px',
    fontWeight: 500,
    color: '#FFFFFF7A',
    lineHeight: 1,
    '& span': {
      fontFamily: 'Poppins',
    },
  },
  gaugeCaption: {
    margin: 0,
    fontFamily: 'Poppins',
    fontSize: 12,
    fontWeight: 500,
    lineHeight: 1.2,
    color: '#FFFFFF7A',
    maxWidth: 110,
    '& span': {
      fontFamily: 'Poppins',
    },
  },
  sectionTitle: {
    margin: `0 0 ${spacing.sm}px`,
    fontFamily: 'Poppins',
    fontSize: fontSize.base,
    fontWeight: 600,
    color: colors.primaryDark,
    '& span': {
      fontFamily: 'Poppins',
    },
  },
  recapGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: spacing.sm,
    marginBottom: spacing.lg,
    [`@media (min-width: ${breakpoints.tablet}px)`]: {
      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    },
  },
  recapCard: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
    padding: '20px 24px',
    backgroundColor: colors.white,
    borderRadius: '4px',
    border: `1px solid ${colors.surfaceBorder}`,
    boxShadow: '0 1px 2px rgba(14, 18, 71, 0.04)',
    boxSizing: 'border-box',
    minHeight: 92,
  },
  recapIcon: {
    width: 56,
    height: 56,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: colors.white,
  },
  recapIconRank1: {
    backgroundColor: '#58B3F8',
  },
  recapIconRank2: {
    backgroundColor: '#268DDB',
  },
  recapIconRank3: {
    backgroundColor: '#0F5890',
  },
  recapFace: {
    width: 32,
    height: 32,
  },
  recapBody: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  recapLabel: {
    margin: 0,
    fontFamily: 'Poppins',
    fontSize: fontSize.sm,
    lineHeight: 1.3,
    color: colors.textSubtle,
    '& span': {
      fontFamily: 'Poppins',
    },
  },
  recapSteps: {
    margin: 0,
    fontFamily: 'Poppins',
    fontSize: fontSize.md,
    lineHeight: 1.35,
    color: colors.textSubtle,
    '& span': {
      fontFamily: 'Poppins',
    },
  },
  recapStepsCount: {
    fontFamily: 'Poppins',
    fontWeight: 700,
    color: colors.primaryDark,
  },
  recapPoints: {
    margin: 0,
    fontFamily: 'Poppins',
    fontSize: fontSize.md,
    lineHeight: 1.35,
    fontWeight: 500,
    color: '#000A8D',
    '& span': {
      fontFamily: 'Poppins',
    },
  },
  detailsIntro: {
    margin: `0 0 ${spacing.md}px`,
    fontFamily: fonts.opensans,
    fontSize: fontSize.md,
    color: colors.text,
    maxWidth: 720,
  },
  group: {
    border: `1px solid ${colors.surfaceBorder}`,
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: colors.white,
    marginBottom: spacing.md,
  },
  groupHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
    padding: '20px 24px',
    backgroundColor: '#DFD8D0A3',
    [`@media (min-width: ${breakpoints.tablet}px)`]: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
  },
  groupTitle: {
    margin: 0,
    fontFamily: 'Poppins',
    fontSize: fontSize.base,
    fontWeight: 600,
    lineHeight: 1.25,
    color: colors.primaryDark,
    '& span': {
      fontFamily: 'Poppins',
    },
  },
  groupPoints: {
    margin: '4px 0 0',
    fontFamily: 'Poppins',
    fontSize: fontSize.sm,
    lineHeight: 1.4,
    color: colors.primaryDark,
    '& span': {
      fontFamily: 'Poppins',
    },
  },
  pills: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexShrink: 0,
    [`@media (max-width: ${breakpoints.tablet - 1}px)`]: {
      alignSelf: 'flex-end',
    },
  },
  pill: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: '#d1d5db',
    flexShrink: 0,
  },
  pillSuccess: {
    backgroundColor: '#22c55e',
  },
  pillError: {
    backgroundColor: '#ef4444',
  },
  pillsLabel: {
    marginLeft: 4,
    fontFamily: 'Poppins',
    fontSize: fontSize.sm,
    color: colors.primaryDark,
    '& span': {
      fontFamily: 'Poppins',
    },
  },
  stepsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: spacing.sm,
    padding: '16px 20px 20px',
    backgroundColor: colors.white,
    [`@media (min-width: ${breakpoints.tablet}px)`]: {
      gridTemplateColumns: '1fr 1fr',
    },
  },
  stepCard: {
    display: 'flex',
    flexDirection: 'column',
    padding: '14px 12px',
    backgroundColor: '#F6F5F3',
    border: `1px solid ${colors.surfaceBorder}`,
    borderRadius: radius.sm,
    textAlign: 'left',
    width: '100%',
    boxSizing: 'border-box',
    font: 'inherit',
  },
  stepHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  stepHeaderMain: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    paddingRight: 8,
  },
  stepNumber: {
    fontFamily: 'Poppins',
    fontSize: fontSize.sm,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    color: '#838183',
    '& span': {
      fontFamily: 'Poppins',
    },
  },
  stepTitle: {
    margin: 0,
    fontFamily: 'Poppins',
    fontSize: fontSize.sm,
    fontWeight: 600,
    lineHeight: 1.3,
    color: colors.primaryDark,
    '& span': {
      fontFamily: 'Poppins',
    },
  },
  stepBody: {
    marginTop: 12,
  },
  choiceBox: {
    padding: '10px 12px',
    borderRadius: 6,
    backgroundColor: colors.white,
    border: `1px solid ${colors.surfaceBorder}`,
    fontFamily: 'Poppins',
    fontSize: fontSize.md,
    color: colors.primaryDark,
    lineHeight: 1.45,
    '& span': {
      fontFamily: 'Poppins',
    },
  },
  choiceNumber: {
    fontFamily: 'Poppins',
    fontWeight: 700,
    fontStyle: 'italic',
    color: colors.primary,
    fontSize: fontSize.sm,
    '& span': {
      fontFamily: 'Poppins',
    },
  },
  choiceLabel: {
    fontFamily: 'Poppins',
    display: 'inline',
    color: colors.text,
    fontSize: fontSize.sm,
    '& p': {
      fontFamily: 'Poppins',
      display: 'inline',
      margin: 0,
    },
  },
}));
